import React from 'react';
import {
    AutocompleteInput,
    Datagrid,
    DateField,
    Filter,
    FunctionField,
    List,
    NumberField,
    ReferenceInput,
    TextField,
} from 'react-admin';
import { Alert } from '@mui/material';

/**
 * Historico de bonos de agentes de IA de un cliente (86cbaa21u).
 *
 * **Sin cliente no se lista nada**: el endpoint es por cliente, y una lista global de bonos de
 * todos los clientes no significa nada para CS.
 *
 * No hay boton de editar ni de borrar, y no es un olvido: renovar es comprar un bono nuevo, que
 * entra en cola, y uno en curso o cerrado es inmutable. Los tres hitos —activado, agotado y
 * liquidado— los sella el sistema.
 */
const STATUS_LABELS = {
    // El bono se compra y espera; arranca el dia que empieza a consumirse, no el de la compra.
    provisioned: 'En cola',
    active: 'Activo',
    closed: 'Liquidado',
};

const Filters = (props) => (
    <Filter {...props}>
        <ReferenceInput
            source="client_id"
            reference="clients"
            alwaysOn
            label="Cliente"
            sort={{ field: 'name', order: 'ASC' }}
            perPage={1000}
        >
            <AutocompleteInput
                source="name"
                optionText="name"
                filterToQuery={(searchText) => ({ name: searchText })}
            />
        </ReferenceInput>
    </Filter>
);

/**
 * El aviso va FUERA del `List` y `empty` esta desactivado a proposito.
 *
 * En react-admin el componente `empty` sustituye la vista entera cuando no hay datos ni filtros
 * aplicados — **barra de filtros incluida**. Al entrar no hay ninguno de los dos, asi que el
 * aviso tapaba el selector de cliente y no habia forma de elegirlo: la pantalla pedia elegir un
 * cliente y no dejaba hacerlo.
 */
const Hint = () => (
    <Alert severity="info" sx={{ mt: 2 }}>
        Elige un cliente para ver sus bonos. Si no tiene ninguno, su consumo se sigue midiendo y es
        facturable como excedente sin bono.
    </Alert>
);

const AiAgentPacksList = () => (
    <>
        <Hint />
        <List filters={<Filters />} empty={false} exporter={false} pagination={false}>
        <Datagrid bulkActionButtons={false}>
            <TextField source="kind" label="Tipo" />
            <TextField source="unit" label="Unidad" />
            <NumberField source="included_units" label="Incluido" />
            <NumberField source="consumed_units" label="Consumido" />
            <DateField source="purchased_at" label="Comprado" />
            {/*
                Un bono en cola todavia no ha tomado el relevo, asi que no tiene fecha de
                activacion. La pone el sistema cuando le toca responder por el consumo: porque no
                habia ningun activo, o porque el que lo estaba se agoto o ya venia excedido cuando
                se compro este.
            */}
            <FunctionField
                label="Activado"
                render={(record) =>
                    record?.activated_at ? new Date(record.activated_at).toLocaleDateString() : '—'
                }
            />
            {/*
                Y esto es otra cosa: cuando empezo de verdad a gastarse. Un bono puede estar
                activado y sin estrenar, y la diferencia entre las dos fechas es el tiempo que
                estuvo esperando a que llegara la primera entrevista.
            */}
            <FunctionField
                label="Primer uso"
                render={(record) =>
                    record?.first_used_at
                        ? new Date(record.first_used_at).toLocaleDateString()
                        : '—'
                }
            />
            <FunctionField
                label="Estado"
                render={(record) => STATUS_LABELS[record?.status] ?? record?.status ?? '—'}
            />
            {/* Un bono puede liquidarse sin haberse agotado nunca: por eso el guion y no un cero. */}
            <FunctionField
                label="Agotado"
                render={(record) =>
                    record?.exhausted_at ? new Date(record.exhausted_at).toLocaleDateString() : '—'
                }
            />
            {/*
                «Liquidado» no es lo contrario de «en vigor», y por eso no hay columna de vigencia:
                la lleva «Estado». El bono que se esta renovando tiene fecha de liquidacion —la del
                que lo sucede, que puede ser futura— y sigue siendo el activo; y el comprado para
                manana no tiene fecha y todavia esta en cola. Deducir la vigencia de `closed_at`,
                como se hacia aqui, pintaba mal las dos filas.
            */}
            <FunctionField
                label="Liquidado"
                render={(record) =>
                    record?.closed_at ? new Date(record.closed_at).toLocaleDateString() : '—'
                }
            />
            <NumberField source="overage_units" label="Excedente" />
            <TextField source="notes" label="Notas" />
        </Datagrid>
        </List>
    </>
);

export default AiAgentPacksList;
